import React, { useState, useEffect, useCallback } from 'react';
import { useSuperAdmin } from '../../contexts/SuperAdminContext';
import { Link } from 'react-router-dom';
import SALayout from './SALayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Building2, Search, Users, ChevronRight, Loader2, IndianRupee } from 'lucide-react';

export default function SAOrganizations() {
  const { apiFetch } = useSuperAdmin();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('');

  const fetchOrgs = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (planFilter) params.set('plan', planFilter);
      const res = await apiFetch(`/organizations?${params}`);
      if (res.ok) setOrgs(await res.json());
    } catch {} finally { setLoading(false); }
  }, [apiFetch, search, planFilter]);

  useEffect(() => { fetchOrgs(); }, [fetchOrgs]);

  const plans = ['', 'trial', 'active', 'expired', 'cancelled', 'free'];

  const getPlanBadge = (plan) => {
    const styles = { active: 'bg-emerald-100 text-emerald-700', trial: 'bg-amber-100 text-amber-700', expired: 'bg-red-100 text-red-700', cancelled: 'bg-slate-200 text-slate-600', free: 'bg-slate-100 text-slate-500' };
    return <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${styles[plan] || styles.free}`}>{plan}</span>;
  };

  return (
    <SALayout>
      <div className="space-y-4" data-testid="sa-organizations">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <h1 className="text-xl font-bold text-slate-900">Organizations ({orgs.length})</h1>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search org..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="h-8 pl-8 text-xs rounded-lg w-48"
                data-testid="sa-org-search"
              />
            </div>
            <select
              value={planFilter}
              onChange={e => setPlanFilter(e.target.value)}
              className="h-8 px-2 text-xs rounded-lg border border-slate-200 bg-white text-slate-700"
              data-testid="sa-org-plan-filter"
            >
              <option value="">All Plans</option>
              {plans.filter(Boolean).map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200/60 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500">Organization</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500">Plan</th>
                  <th className="text-center px-4 py-2.5 text-xs font-medium text-slate-500">Members</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500 hidden md:table-cell">Trial Ends</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500 hidden md:table-cell">Price/User</th>
                  <th className="text-right px-4 py-2.5 text-xs font-medium text-slate-500">Total Spend</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500 hidden lg:table-cell">Promo</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500">Status</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {orgs.map(org => (
                  <tr key={org.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-3.5 h-3.5 text-slate-500" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900 text-xs truncate max-w-[180px]">{org.name}</p>
                          <p className="text-[10px] text-slate-400">{new Date(org.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">{getPlanBadge(org.plan)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-xs text-slate-600 flex items-center justify-center gap-1">
                        <Users className="w-3 h-3" /> {org.member_count}/{org.seat_limit}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500 hidden md:table-cell">
                      {org.trial_ends_at ? new Date(org.trial_ends_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600 hidden md:table-cell">
                      {org.effective_price !== org.effective_price ? '-' : `₹${org.effective_price}`}
                    </td>
                    <td className="px-4 py-3 text-right" data-testid={`sa-org-spend-${org.id}`}>
                      {org.total_spend > 0 ? (
                        <span className="text-xs font-semibold text-green-700">₹{org.total_spend.toLocaleString('en-IN')}</span>
                      ) : (
                        <span className="text-xs text-slate-300">₹0</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs hidden lg:table-cell">
                      {org.promo_code ? <span className="text-purple-600 font-medium">{org.promo_code}</span> : <span className="text-slate-300">-</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`w-2 h-2 rounded-full inline-block ${org.is_active ? 'bg-emerald-500' : 'bg-red-400'}`} />
                    </td>
                    <td className="px-4 py-3">
                      <Link to={`/superadmin/organizations/${org.id}`} data-testid={`sa-org-detail-${org.id}`}>
                        <ChevronRight className="w-4 h-4 text-slate-400 hover:text-slate-600" />
                      </Link>
                    </td>
                  </tr>
                ))}
                {orgs.length === 0 && (
                  <tr><td colSpan="9" className="px-4 py-12 text-center text-sm text-slate-400">No organizations found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </SALayout>
  );
}
